/**
 * ImageLightbox — click any thumbnail to view it large in a branded modal.
 *
 * Usage:
 *   const openImage = useLightbox();
 *   <img src={url} onClick={() => openImage(url)} className="cursor-zoom-in" />
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

const LightboxContext = createContext(null);

export function ImageLightboxProvider({ children }) {
  const [src, setSrc] = useState(null);
  const openImage = useCallback((url) => setSrc(url), []);
  const close = useCallback(() => setSrc(null), []);

  return (
    <LightboxContext.Provider value={openImage}>
      {children}
      <Lightbox src={src} onClose={close} />
    </LightboxContext.Provider>
  );
}

export function useLightbox() {
  const ctx = useContext(LightboxContext);
  if (!ctx) throw new Error("useLightbox must be used within an ImageLightboxProvider");
  return ctx;
}

function Lightbox({ src, onClose }) {
  const open = !!src;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[130] flex items-center justify-center p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
          onClick={onClose}
          data-testid="image-lightbox-overlay"
        >
          <div className="absolute inset-0 bg-[#1c1c1c]/80 backdrop-blur-[3px]" />
          <motion.img
            src={src}
            alt=""
            className="relative max-w-[92vw] max-h-[88vh] object-contain rounded-lg shadow-2xl"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="image-lightbox-img"
          />
          <button
            onClick={onClose}
            className="absolute top-5 right-5 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2 transition-colors"
            data-testid="image-lightbox-close"
            title="Close"
          >
            <X size={22} />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
